import React, { useState, useEffect } from "react";

const CookiesPolicyBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user has accepted cookies in the last 24 hours
    const cookieConsentTimestamp = sessionStorage.getItem("cookieConsentTimestamp");

    if (cookieConsentTimestamp) {
      const acceptedTime = parseInt(cookieConsentTimestamp, 10);
      const currentTime = new Date().getTime();
      const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

      // If less than 24 hours have passed, don't show banner
      if (currentTime - acceptedTime < twentyFourHours) {
        setIsVisible(false);
      } else {
        // If more than 24 hours have passed, clear session storage and show banner
        sessionStorage.removeItem("cookieConsentTimestamp");
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }

    // Check screen size
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const clearAllData = () => {
    // Clear session storage
    sessionStorage.clear();

    // Clear local storage
    localStorage.clear();

    // Clear all cookies
    const cookies = document.cookie.split(";");

    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;

      // Delete cookie for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      // Delete cookie for current domain and all subdomains
      const domain = window.location.hostname;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;

      // Try to delete for parent domain as well
      const domainParts = domain.split(".");
      if (domainParts.length > 2) {
        const parentDomain = `.${domainParts.slice(-2).join(".")}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${parentDomain}`;
      }
    });
  };

  const handleAccept = () => {
    // Save timestamp in session storage (expires after 24 hours or when browser closes)
    const currentTime = new Date().getTime();
    sessionStorage.setItem("cookieConsentTimestamp", currentTime.toString());
    setIsVisible(false);
  };

  const handleDecline = () => {
    // Clear all data
    clearAllData();

    // Redirect to Google
    window.location.href = "https://www.google.com";
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "#ffffff",
        boxShadow: "0px 2px 24px 0px rgba(0,0,0,0.06)",
        zIndex: 9999,
        padding: isMobile ? "24px" : "24px 135px",
      }}
      className="cookies-policy-banner"
    >
      <div
        style={{
          display: "flex",
          gap: isMobile ? "10px" : "24px",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "space-between",
          marginBottom: "16px",
        }}
      >
        <p
          style={{
            flex: isMobile ? "0 1 auto" : "1",
            width: isMobile ? "301px" : "auto",
            fontFamily: "Poppins, sans-serif",
            fontWeight: "600",
            fontSize: "20px",
            lineHeight: "24px",
            color: "#252b1a",
            margin: 0,
          }}
        >
          Privacy Notice
        </p>
        <button
          type="button"
          onClick={handleClose}
          style={{
            width: "16px",
            height: "16px",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-label="Close"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="#252b1a"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "16px" : "108px",
          alignItems: "center",
        }}
      >
        <div style={{ flex: isMobile ? "0 0 auto" : "1" }}>
          <p
            style={{
              fontFamily: "Roboto, sans-serif",
              fontWeight: "400",
              fontSize: "18px",
              lineHeight: "30px",
              color: "#000000",
              margin: 0,
              width: "100%",
            }}
          >
            This website uses cookies to improve your experience on the site. You can choose to
            accept or decline their use at any time.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            gap: "16px",
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "auto" : "174px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={isMobile ? handleDecline : handleAccept}
            style={{
              backgroundColor: isMobile ? "transparent" : "#5d6d41",
              color: isMobile ? "#5d6d41" : "#ffffff",
              fontFamily: "Roboto, sans-serif",
              fontWeight: "500",
              fontSize: "14px",
              padding: "14px 30px",
              border: isMobile ? "1.5px solid #5d6d41" : "none",
              cursor: "pointer",
              height: "38px",
              width: isMobile ? "auto" : "174px",
              flex: isMobile ? "1" : "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isMobile ? "Decline" : "Accept"}
          </button>
          <button
            type="button"
            onClick={isMobile ? handleAccept : handleDecline}
            style={{
              backgroundColor: isMobile ? "#5d6d41" : "transparent",
              color: isMobile ? "#ffffff" : "#5d6d41",
              fontFamily: "Roboto, sans-serif",
              fontWeight: "500",
              fontSize: "14px",
              padding: "14px 30px",
              border: isMobile ? "none" : "1.5px solid #5d6d41",
              cursor: "pointer",
              height: "38px",
              width: isMobile ? "auto" : "174px",
              flex: isMobile ? "1" : "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isMobile ? "Accept" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicyBanner;
